// Version Bump 1160
// ^ Bump this to and commit if needing to re-deploy managed-apps changes with no other changes in the commit.
/* ci-version: 2.3.2132 */
// ^ Auto-managed by ci/src/stages/versionBump.ts — do not edit by hand.
//   Swapped to the current shared/package.json version on every CI version bump
//   so Netlify detects an admin-app content change and redeploys the frontend
//   even when a commit only touches backend/managed-apps files.
// import './wdyr-alternative2.js'; // <--- first import per https://github.com/welldone-software/why-did-you-render
// import './utils/polyfills/process.js'; // Polyfill process for shared code

// MUST stay the first import: neutralizes React 19's dev-only per-commit
// performance.measure() flood (logComponentRender) before react-dom initializes,
// which otherwise OOMs heavy pages like the agents grid. See the file header.
import './disableReactPerfTracks.js';

// MATM-1271: define window.__matmBootMark so the cold-boot phase-split marks
// already sprinkled across the shim / App shells actually land somewhere in a
// real build (they were no-ops outside the Playwright harness). Installed as
// early as possible so it captures the shim marks. Cheap-flag gated + deferred
// shipping — see bootMarkCollector.js. Must run before localAppShim (imported
// below) emits its first mark.

import LogRocket, {
	ensureLogRocketLoaded,
} from 'shared/services/LogRocket';

//! NOTE: DISABLED until logrocket-react releases an updated supporting React 19
// import setupLogRocketReact from 'logrocket-react';

import hasOwn from 'object.hasown';
import AppConfig from 'shared/config-public';

import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import { defineCustomElements } from '@ionic/pwa-elements/loader';

import sayHello from 'utils/sayHello.js';
import { installBootMarkCollector } from './utils/bootMarkCollector.js';
import { installTailwindFreshnessDevHelper } from './utils/installTailwindFreshnessDevHelper.ts';
import { installDevBundleFreshnessCheck } from './utils/installDevBundleFreshnessCheck.ts';
import localAppShim from './localAppShim.js';
import reportWebVitals from './reportWebVitals';
// BC-1586: offline-journal PWA service worker (branded-origin-gated; no-op off-allowlist).
import { registerOfflineServiceWorker } from './registerOfflineSw.js';

// MATM-1271: install the cold-boot mark collector first thing so every
// window.__matmBootMark(...) call from the shim/App shells is captured. No-op if
// the Playwright harness already injected its own collector at document-start.
installBootMarkCollector();

// Older iPhones (<12) sometimes don't have Object.hasOwn
if (!Object.hasOwn) {
	hasOwn.shim();
}

// BDL-2808: a dev-only window.assertTailwindFresh(candidate, control)
// helper -- see installTailwindFreshnessDevHelper.ts. No-ops outside dev/
// staging (never installed on the prod bundle).
installTailwindFreshnessDevHelper();

// BDL-1609: warn loudly when this dev server is serving a bundle older than
// the source on disk -- the condition that made ~11h of browser verification
// confidently wrong. Also exposes window.assertBundleFresh(). No-ops outside
// dev/staging (never installed on the prod bundle).
installDevBundleFreshnessCheck();

sayHello();

// Prevent scroll-to-change on number inputs - users accidentally change values when scrolling
document.addEventListener(
	'wheel',
	() => {
		if (document.activeElement?.type === 'number') {
			document.activeElement.blur();
		}
	},
	{ passive: true },
);

// Only loading LogRocket in prod to save on logging costs. The import above
// resolves to a no-op stub — ensureLogRocketLoaded() dynamically loads the
// real `logrocket` package (~200KB) in prod only.
if (AppConfig.buildEnv === 'prod') {
	ensureLogRocketLoaded().then(() => {
		LogRocket.init(`xmpbov/vaya-app-v2`, {
			// Props available documented: https://docs.logrocket.com/v1.0/reference/init
			mergeIframes: true, // ref: https://docs.logrocket.com/reference/mergeiframes
			release: AppConfig.version, // ref: https://docs.logrocket.com/reference/release
		});

		// Ref: https://docs.logrocket.com/reference/react-plugin-1
		//! NOTE: DISABLED until logrocket-react releases an updated supporting React 19
		// setupLogRocketReact(LogRocket);
	});
}

/**
 * MAJOR MAJOR HACK
 * For whatever reason, styled-components don't work unless
 * we mount one FIRST here. THEN it will work elsewhere in the app.
 * Without a simple SC here, styled components just...don't...work. WHY?
 */
const StyledDiv = styled.div`
	background: transparent;
`;

const root = createRoot(document.getElementById('root'));
root.render(<StyledDiv />);

// Hack done...now render the app like usual
// Call the shim to load the app (e.g. allow OTA updates)
localAppShim({ root });

// ref: https://capacitorjs.com/docs/web/pwa-elements
// Call the element loader after the app has been rendered the first time
defineCustomElements(window);

// BC-1586: register the offline-journal service worker. Internally gated to the
// `offlineSwHostAllowlist` hosts, so this is a no-op on every other origin.
registerOfflineServiceWorker();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// eslint-disable-next-line no-console
reportWebVitals(AppConfig.buildEnv === 'prod' ? console.log : undefined);
